import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  Group,
  ICreateGroupRequest,
  ICreateGroupResponse,
  IDeleteGroupRequest,
  IEditGroupRequest,
  IEditGroupResponse,
  IGetGroupRequest,
  IGetGroupResponse,
  IGetGroupsRequest,
  IGetGroupsResponse,
} from 'redux/types/AdvancedPlanner.types';
import { IFailedPayload } from 'redux/types/Allocation.types';

export interface AdvancedPlannerState {
  groups: Group[];
  currentGroup: Group | null;
  groupsLoader: boolean;
  error: IFailedPayload | null;
}

export const advancedPlannerInitState: AdvancedPlannerState = {
  groups: [],
  currentGroup: null,
  groupsLoader: false,
  error: null,
};

const advancedPlannerSlice = createSlice({
  name: 'advancedPlanner',
  initialState: advancedPlannerInitState,
  reducers: {
    fetchGroups: (state, action: PayloadAction<IGetGroupsRequest>) => {
      state.groupsLoader = true;
      state.error = null;
    },
    fetchGroupsSuccess: (state, action: PayloadAction<IGetGroupsResponse>) => {
      state.groups = action.payload;
      state.groupsLoader = false;
    },
    fetchGroupsFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.groupsLoader = false;
    },

    fetchGroup: (state, action: PayloadAction<IGetGroupRequest>) => {
      state.groupsLoader = true;
      state.error = null;
    },
    fetchGroupSuccess: (state, action: PayloadAction<IGetGroupResponse>) => {
      state.currentGroup = action.payload;
      state.groupsLoader = false;
    },
    fetchGroupFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.groupsLoader = false;
    },

    createGroup: (state, action: PayloadAction<ICreateGroupRequest>) => {
      state.groupsLoader = true;
      state.error = null;
    },
    createGroupSuccess: (
      state,
      action: PayloadAction<ICreateGroupResponse>
    ) => {
      state.groups.push({
        group: action.payload,
        ...action.payload,
      } as Group);
      state.groupsLoader = false;
    },
    createGroupFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.groupsLoader = false;
    },

    editGroup: (state, action: PayloadAction<IEditGroupRequest>) => {
      state.groupsLoader = true;
      state.error = null;
    },
    editGroupSuccess: (state, action: PayloadAction<IEditGroupResponse>) => {
      const index = state.groups.findIndex(
        (group) => group.groupId === action.payload.groupId
      );
      if (index !== -1) {
        state.groups[index] = {
          ...state.groups[index],
          ...action.payload,
        };
      }
      state.groupsLoader = false;
    },
    editGroupFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.groupsLoader = false;
    },

    deleteGroup: (state, action: PayloadAction<IDeleteGroupRequest>) => {
      state.groupsLoader = true;
      state.error = null;
    },
    deleteGroupSuccess: (state, action: PayloadAction<string | number>) => {
      state.groups = state.groups.filter(
        (group) => group.groupId !== action.payload
      );
      state.groupsLoader = false;
    },
    deleteGroupFailure: (state, action: PayloadAction<IFailedPayload>) => {
      state.error = action.payload;
      state.groupsLoader = false;
    },
  },
});

export const {
  fetchGroups,
  fetchGroupsSuccess,
  fetchGroupsFailure,
  fetchGroup,
  fetchGroupSuccess,
  fetchGroupFailure,
  createGroup,
  createGroupSuccess,
  createGroupFailure,
  editGroup,
  editGroupSuccess,
  editGroupFailure,
  deleteGroup,
  deleteGroupSuccess,
  deleteGroupFailure,
} = advancedPlannerSlice.actions;

export default advancedPlannerSlice.reducer;
